import React, { useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Home, FileText, Send, CheckCircle } from 'lucide-react';

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  propertyType: string;
  plotSize: string;
  plotSizeUnit: string;
  budget: string;
  location: string;
  timeline: string;
  additionalInfo: string;
}

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LeadFormModal: React.FC<LeadFormModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    plotSize: '',
    plotSizeUnit: 'marla',
    budget: '',
    location: '',
    timeline: '',
    additionalInfo: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(API_ENDPOINTS.leads, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Track Facebook Lead conversion
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Lead', {
            content_name: 'Property Requirements Form',
            content_category: 'Lead Generation',
            value: 1,
            currency: 'USD'
          });
        }
        
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          onClose();
          setFormData({
            name: '',
            email: '',
            phone: '',
            propertyType: '',
            plotSize: '',
            plotSizeUnit: 'marla',
            budget: '',
            location: '',
            timeline: '',
            additionalInfo: ''
          });
        }, 3000);
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Network error. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden"
          >
            <div className="bg-gradient-to-br from-white/90 to-white/80 backdrop-blur-xl border border-white/20 rounded-2xl shadow-glass overflow-hidden">
              <div className="bg-gradient-to-r from-neon-blue/20 to-primary-600/20 p-6 border-b border-white/20">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                      Property Requirements
                    </h2>
                    <p className="text-gray-600 mt-1">Tell us about your dream property</p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-xl hover:bg-white/50 transition-colors"
                  >
                    <X className="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <User className="h-5 w-5 mr-2 text-neon-blue" />
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                            placeholder="John Doe"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                          placeholder="+92 300 1234567"
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Home className="h-5 w-5 mr-2 text-neon-blue" />
                        Property Requirements
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Type *
                          </label>
                          <select
                            name="propertyType"
                            required
                            value={formData.propertyType}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                          >
                            <option value="">Select Property Type</option>
                            <option value="residential">Residential</option>
                            <option value="commercial">Commercial</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Budget Range (PKR) *
                          </label>
                          <select
                            name="budget"
                            required
                            value={formData.budget}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                          >
                            <option value="">Select Budget Range</option>
                            <option value="0-10lakh">Under 10 Lakh</option>
                            <option value="10lakh-20lakh">10 - 20 Lakh</option>
                            <option value="20lakh-30lakh">20 - 30 Lakh</option>
                            <option value="30lakh-40lakh">30 - 40 Lakh</option>
                            <option value="40lakh-50lakh">40 - 50 Lakh</option>
                            <option value="50lakh-60lakh">50 - 60 Lakh</option>
                            <option value="60lakh-70lakh">60 - 70 Lakh</option>
                            <option value="70lakh-80lakh">70 - 80 Lakh</option>
                            <option value="80lakh-90lakh">80 - 90 Lakh</option>
                            <option value="90lakh-1cr">90 Lakh - 1 Crore</option>
                            <option value="1cr-2cr">1 - 2 Crore</option>
                            <option value="2cr-3cr">2 - 3 Crore</option>
                            <option value="3cr-4cr">3 - 4 Crore</option>
                            <option value="4cr-5cr">4 - 5 Crore</option>
                            <option value="5cr-6cr">5 - 6 Crore</option>
                            <option value="6cr-7cr">6 - 7 Crore</option>
                            <option value="7cr-8cr">7 - 8 Crore</option>
                            <option value="8cr-9cr">8 - 9 Crore</option>
                            <option value="9cr-10cr">9 - 10 Crore</option>
                            <option value="10cr+">Above 10 Crore</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Plot Size *
                          </label>
                          <input
                            type="number"
                            name="plotSize"
                            required
                            value={formData.plotSize}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                            placeholder="5"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Unit *
                          </label>
                          <select
                            name="plotSizeUnit"
                            required
                            value={formData.plotSizeUnit}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                          >
                            <option value="marla">Marla</option>
                            <option value="kanal">Kanal</option>
                            <option value="sqft">Square Feet</option>
                            <option value="acre">Acre</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Timeline *
                          </label>
                          <select
                            name="timeline"
                            required
                            value={formData.timeline}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                          >
                            <option value="">Select Timeline</option>
                            <option value="immediate">Immediate</option>
                            <option value="1month">Within 1 Month</option>
                            <option value="3months">Within 3 Months</option>
                            <option value="6months">Within 6 Months</option>
                            <option value="1year">Within 1 Year</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Preferred Location *
                        </label>
                        <select
                          name="location"
                          required
                          value={formData.location}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                        >
                          <option value="">Select Location</option>
                          <option value="faisal-hills">Faisal Hills</option>
                          <option value="faisal-town-phase1">Faisal Town Phase 1</option>
                          <option value="faisal-town-phase2">Faisal Town Phase 2</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-neon-blue" />
                        Additional Information
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Specific Requirements
                        </label>
                        <textarea
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-white/20 rounded-xl focus:ring-2 focus:ring-neon-blue focus:border-transparent transition-all duration-300"
                          placeholder="Any specific requirements, preferences, or additional details..."
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-4 pt-4">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-3 bg-gradient-to-r from-neon-blue to-primary-600 text-white rounded-xl hover:shadow-neon-blue transition-all duration-300 font-medium flex items-center"
                      >
                        <Send className="h-5 w-5 mr-2" />
                        Submit Requirements
                      </button>
                    </div>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Thank You!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Your property requirements have been submitted successfully. Our team will contact you within 24 hours.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Next Steps:</strong> Our property advisor will review your requirements and reach out to schedule a consultation.
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LeadFormModal;